import React, { useState, useEffect } from 'react';
import { 
  WifiIcon, 
  SignalSlashIcon, 
  ArrowPathIcon, 
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useOffline, useOfflineStatus } from '@/hooks/useOffline';
import { useConnection } from '@/contexts/ConnectionContext';

interface OfflineIndicatorProps {
  className?: string;
  showDetails?: boolean;
  onSyncClick?: () => void;
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({ 
  className = '',
  showDetails = false,
  onSyncClick
}) => {
  const { isOnline } = useConnection(); // Utiliser le contexte global
  const { isOfflineMode, toggleOfflineMode } = useOffline();
  const { statusMessage, statusColor, canSync, pendingChanges, lastSync } = useOfflineStatus();
  const [showDetailsPanel, setShowDetailsPanel] = useState(showDetails);
  const [isToggling, setIsToggling] = useState(false);

  // Force la mise à jour périodique pour éviter les problèmes de cache
  const [, setForceUpdate] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setForceUpdate(prev => prev + 1);
    }, 2000); // Mise à jour toutes les 2 secondes
    
    return () => clearInterval(interval);
  }, []);

  // Gestionnaire pour la reconnexion
  const handleConnectionRestored = () => {
    setForceUpdate(prev => prev + 1);
  };
  
  // Gestion des événements de reconnexion
  useEffect(() => {
    window.addEventListener('connection-restored', handleConnectionRestored as EventListener);
    
    return () => {
      window.removeEventListener('connection-restored', handleConnectionRestored as EventListener);
    };
  }, []);

  const handleToggleOfflineMode = async () => {
    setIsToggling(true);
    try {
      await toggleOfflineMode();
    } finally {
      setIsToggling(false);
    }
  };

  const handleSyncClick = () => {
    if (onSyncClick) {
      onSyncClick();
    } else {
      // Déclencher la synchronisation par défaut
      window.dispatchEvent(new CustomEvent('request-sync'));
    }
  };

  const getStatusIcon = () => {
    if (isOnline && !isOfflineMode) {
      return <WifiIcon className="h-5 w-5 text-green-500" />;
    }
    if (isOfflineMode) {
      return <SignalSlashIcon className="h-5 w-5 text-orange-500" />;
    }
    if (!isOnline) {
      return <SignalSlashIcon className="h-5 w-5 text-red-500" />;
    }
    return <InformationCircleIcon className="h-5 w-5 text-gray-500" />;
  };

  const getStatusBgColor = () => {
    switch (statusColor) {
      case 'green': return 'bg-green-500';
      case 'orange': return 'bg-orange-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  // Afficher seulement si :
  // - On est hors ligne (!isOnline)
  // - On est en mode offline manuel (isOfflineMode)
  // - Il y a des changements en attente (pendingChanges > 0)
  const shouldShow = !isOnline || isOfflineMode || pendingChanges > 0;
  if (!shouldShow) {
    return null;
  }

  return (
    <div className={`fixed top-0 left-0 right-0 z-50 ${getStatusBgColor()} text-white shadow-lg ${className}`}>
      <div className="px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {getStatusIcon()}
            <div className="flex flex-col">
              <span className="text-sm font-medium">
                {!isOnline ? 'Hors Ligne' : 
                 isOfflineMode ? 'Mode Offline Manuel' : 
                 'Changements en Attente'}
              </span>
              <span className="text-xs opacity-90">
                {statusMessage}
              </span>
            </div>
            
            {pendingChanges > 0 && (
              <div className="flex items-center space-x-2">
                <div className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                  {pendingChanges}
                </div>
                <span className="text-xs">
                  {pendingChanges === 1 ? 'changement' : 'changements'} en attente
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2">
            {/* Bouton de synchronisation */}
            {canSync && (
              <button
                onClick={handleSyncClick}
                className="flex items-center space-x-1 bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-sm transition-colors"
                title="Synchroniser les données"
              >
                <ArrowPathIcon className="h-4 w-4" />
                <span>Sync</span>
              </button>
            )}

            {/* Bouton de basculement du mode offline */}
            <button
              onClick={handleToggleOfflineMode}
              disabled={isToggling}
              className="flex items-center space-x-1 bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-sm transition-colors disabled:opacity-50"
              title={isOfflineMode ? 'Passer en mode en ligne' : 'Passer en mode offline'}
            >
              {isToggling ? (
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
              ) : (
                <SignalSlashIcon className="h-4 w-4" />
              )}
              <span>{isOfflineMode ? 'En ligne' : 'Offline'}</span>
            </button>

            {/* Bouton de détails */}
            <button
              onClick={() => setShowDetailsPanel(!showDetailsPanel)}
              className="flex items-center space-x-1 bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded text-sm transition-colors"
              title="Afficher les détails"
            >
              <InformationCircleIcon className="h-4 w-4" />
              <span>Détails</span>
            </button>
          </div>
        </div>

        {/* Panel de détails */}
        {showDetailsPanel && (
          <div className="mt-3 pt-3 border-t border-white border-opacity-20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-medium mb-2">État de la connexion</h4>
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <WifiIcon className="h-4 w-4" />
                    <span>Internet: {isOnline ? 'Connecté' : 'Déconnecté'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <SignalSlashIcon className="h-4 w-4" />
                    <span>Mode: {isOfflineMode ? 'Offline manuel' : 'Automatique'}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-2">Synchronisation</h4>
                <div className="space-y-1">
                  <div className="flex items-center space-x-2">
                    <ArrowPathIcon className="h-4 w-4" />
                    <span>Dernière sync: {lastSync ? lastSync.toLocaleString() : 'Jamais'}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {pendingChanges > 0 ? (
                      <ExclamationTriangleIcon className="h-4 w-4 text-yellow-400" />
                    ) : (
                      <CheckCircleIcon className="h-4 w-4 text-green-400" />
                    )}
                    <span>Changements: {pendingChanges}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions rapides */}
            <div className="mt-4 pt-3 border-t border-white border-opacity-20">
              <div className="flex flex-wrap gap-2">
                {canSync && (
                  <button
                    onClick={handleSyncClick}
                    className="flex items-center space-x-1 bg-green-600 hover:bg-green-700 px-3 py-1 rounded text-sm transition-colors"
                  >
                    <ArrowPathIcon className="h-4 w-4" />
                    <span>Synchroniser maintenant</span>
                  </button>
                )}

                <button
                  onClick={() => window.dispatchEvent(new CustomEvent('open-offline-management'))}
                  className="flex items-center space-x-1 bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded text-sm transition-colors"
                >
                  <InformationCircleIcon className="h-4 w-4" />
                  <span>Gérer offline</span>
                </button>

                <button
                  onClick={() => setShowDetailsPanel(false)}
                  className="flex items-center space-x-1 bg-gray-600 hover:bg-gray-700 px-3 py-1 rounded text-sm transition-colors"
                >
                  <XCircleIcon className="h-4 w-4" />
                  <span>Fermer</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Composant compact pour la barre de navigation
export const OfflineStatusBadge: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { isOnline } = useConnection(); // Utiliser le contexte global
  const { isOfflineMode } = useOffline();
  const { pendingChanges } = useOfflineStatus();

  // Force la mise à jour périodique pour éviter les problèmes de cache
  const [, setForceUpdate] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setForceUpdate(prev => prev + 1);
    }, 1000); // Mise à jour toutes les secondes
    
    return () => clearInterval(interval);
  }, []);

  // Debug: Log des changements d'état
  useEffect(() => {
    console.log('🔄 OfflineStatusBadge - État changé:', { 
      isOnline, 
      isOfflineMode, 
      pendingChanges,
      timestamp: new Date().toLocaleTimeString()
    });
  }, [isOnline, isOfflineMode, pendingChanges]);

  return (
    <div className={`flex items-center space-x-2 ${className}`}>
      <div className={`w-2 h-2 rounded-full ${
        isOnline && !isOfflineMode ? 'bg-green-500' :
        isOfflineMode ? 'bg-orange-500' : 'bg-red-500'
      }`} />
      
      <span className="text-sm">
        {isOnline && !isOfflineMode ? 'En ligne' : 
         isOfflineMode ? 'Offline' : 'Hors ligne'}
        {pendingChanges > 0 && ` (${pendingChanges})`}
      </span>
    </div>
  );
};

// Composant pour les notifications de synchronisation
export const SyncNotification: React.FC = () => {
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
    visible: boolean;
  }>({ type: 'info', message: '', visible: false });

  // Force la mise à jour périodique pour éviter les problèmes de cache
  const [, setForceUpdate] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setForceUpdate(prev => prev + 1);
    }, 5000); // Mise à jour toutes les 5 secondes
    
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    console.log('🔄 SyncNotification - Mise à jour:', { 
      visible: notification.visible,
      type: notification.type,
      timestamp: new Date().toLocaleTimeString()
    });
  }, [notification]);

  useEffect(() => {
    const handleSyncSuccess = (event: CustomEvent) => {
      const result = event.detail;
      setNotification({
        type: 'success',
        message: `Synchronisation réussie: ${result.uploaded} uploads, ${result.downloaded} téléchargements`,
        visible: true
      });
      
      setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 5000);
    };

    const handleSyncError = (event: CustomEvent) => {
      const error = event.detail;
      setNotification({
        type: 'error',
        message: `Erreur de synchronisation: ${error.message || 'Erreur inconnue'}`,
        visible: true
      });
      
      setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 5000);
    };

    window.addEventListener('sync-success', handleSyncSuccess as EventListener);
    window.addEventListener('sync-error', handleSyncError as EventListener);

    return () => {
      window.removeEventListener('sync-success', handleSyncSuccess as EventListener);
      window.removeEventListener('sync-error', handleSyncError as EventListener);
    };
  }, []);

  if (!notification.visible) return null;

  return (
    <div className={`fixed top-16 right-4 z-50 p-4 rounded-lg shadow-lg max-w-sm ${
      notification.type === 'success' ? 'bg-green-500 text-white' :
      notification.type === 'error' ? 'bg-red-500 text-white' :
      'bg-blue-500 text-white'
    }`}>
      <div className="flex items-start space-x-2">
        {notification.type === 'success' && <CheckCircleIcon className="h-5 w-5 flex-shrink-0" />}
        {notification.type === 'error' && <XCircleIcon className="h-5 w-5 flex-shrink-0" />}
        {notification.type === 'info' && <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />}
        
        <div className="flex-1">
          <p className="text-sm font-medium">{notification.message}</p>
        </div>
        
        <button
          onClick={() => setNotification(prev => ({ ...prev, visible: false }))}
          className="flex-shrink-0 ml-2 text-white hover:text-gray-200"
        >
          <XCircleIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};
